import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type SubstitutedInvoiceRequestBody =
  paths['/substituted-invoices']['post']['requestBody']['content']['application/json'];
export type SubstitutedInvoiceResponse =
  paths['/substituted-invoices']['post']['responses']['200']['content']['application/json'];
export type SubstitutedInvoice = SubstitutedInvoiceResponse['data'][0];

interface SubstitutedInvoiceParams {
  makerOrganizationUuid: string;
  search?: string;
  takerMarkets: TakerMarket[];
}

const fetchSubstitutedInvoices = async ({
  makerOrganizationUuid,
  search,
  takerMarkets,
}: SubstitutedInvoiceParams): Promise<SubstitutedInvoice[]> => {
  const response = await apiClient
    .post('api/supplier-experience/substituted-invoices', {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        })),
        ...(search ? { search: { voucherPatterns: [search] } } : {}),
      } satisfies SubstitutedInvoiceRequestBody,
    })
    .json<SubstitutedInvoiceResponse>();

  return response.data;
};

/**
 * Instant Pay substituted invoices for one customer's supplier/market pairs (Invoice Central
 * Substitutions tab). The route isn't paginated, so this returns every match and the table pages
 * client-side. `search` is a case-insensitive contains match on the invoice ID.
 */
const useSubstitutedInvoices = (params: SubstitutedInvoiceParams) => {
  const { makerOrganizationUuid, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['substituted-invoices', makerOrganizationUuid, search, ...takerMarketUuidsMap]
      : ['substituted-invoices-invalid'],
    queryFn: () => (isValid ? fetchSubstitutedInvoices(params) : []),
  });
};

export default useSubstitutedInvoices;
